import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiServices from "../../service";

export interface FileLinkItem {
  id: string;
  title: string;
  description: string;
  fileSource: string;
  fileLink: string;
  type: "file" | "folder";
  created_at?: string;
}

interface FileLinkState {
  list: FileLinkItem[];
  loading: boolean;
  error: string | null;
  total: number;
}

const initialState: FileLinkState = {
  list: [],
  loading: false,
  error: null,
  total: 0,
};

function resolveType(fileLink: string): "file" | "folder" {
  return /[/\\]{1,2}$/.test(fileLink) ? "folder" : "file";
}

export const fetchFileLinks = createAsyncThunk<
  { list: FileLinkItem[]; total: number },
  { customerId: string; offset?: number; count?: number },
  { rejectValue: string }
>("fileLink/fetchAll", async ({ customerId, offset = 0, count = 10 }, { rejectWithValue }) => {
  try {
    const res = await apiServices.fileLink.getList(customerId, offset, count);
    const data: any[] = res.data.data ?? [];
    const list: FileLinkItem[] = data.map((item) => ({
      ...item,
      type: resolveType(item.fileLink),
    }));
    return { list, total: res.data.pagination?.total ?? list.length };
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || "Failed to fetch file links");
  }
});

export const addFileLink = createAsyncThunk<
  FileLinkItem,
  { data: any },
  { rejectValue: string }
>("fileLink/add", async ({ data }, { rejectWithValue }) => {
  try {
    const res = await apiServices.fileLink.postFileLink(data);
    const item = res.data.data;
    return { ...item, type: resolveType(item.fileLink) };
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || "Failed to add file link");
  }
});

export const updateFileLink = createAsyncThunk<
  FileLinkItem,
  { id: string; data: any },
  { rejectValue: string }
>("fileLink/update", async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await apiServices.fileLink.updateFileLink(id, data);
    const item = res.data.data;
    return { ...item, type: resolveType(item.fileLink) };
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || "Failed to update file link");
  }
});

export const deleteFileLink = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("fileLink/delete", async (id, { rejectWithValue }) => {
  try {
    await apiServices.fileLink.deleteFileLink(id);
    return id;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || "Failed to delete file link");
  }
});

const fileLinkSlice = createSlice({
  name: "fileLink",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFileLinks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFileLinks.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.list;
        state.total = action.payload.total;
      })
      .addCase(fetchFileLinks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "An error occurred";
      })
      .addCase(addFileLink.fulfilled, (state, action) => {
        state.list.push(action.payload);
        state.total += 1;
      })
      .addCase(updateFileLink.fulfilled, (state, action) => {
        const idx = state.list.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.list[idx] = action.payload;
      })
      .addCase(deleteFileLink.fulfilled, (state, action) => {
        state.list = state.list.filter((i) => i.id !== action.payload);
        state.total -= 1;
      });
  },
});

export default fileLinkSlice.reducer;
